/**
 * Flow step screen_config logic, shared by /admin/workshop (Näytöt tab,
 * screen state editor) and PlayView.
 *
 * screen_config formats (oldest to newest):
 *  - legacy:      { walls: { view } }                      same view everywhere
 *  - grouped:     { screens: { primary: {..}, secondary: {..} } }
 *  - per-screen:  { A1: { view, params }, A2: {...}, ... } (direct or under .screens)
 *
 * The designer always writes per-screen format; the resolver accepts all three.
 */

export const WALLS = [
  { id: 'A', name: 'Pohjoinen', screens: ['A1', 'A2'] },
  { id: 'B', name: 'Itä', screens: ['B1', 'B2'] },
  { id: 'C', name: 'Etelä', screens: ['C1', 'C2'] },
  { id: 'D', name: 'Länsi', screens: ['D1', 'D2'] },
];

export const PRIMARY_SCREENS = ['A1', 'B1', 'C1', 'D1'];
export const SECONDARY_SCREENS = ['A2', 'B2', 'C2', 'D2'];
export const ALL_SCREENS = WALLS.flatMap(w => w.screens);

/** screen_config comes from the DB as JSONB (object) or occasionally a string. */
export function parseScreenConfig(step) {
  if (!step) return {};
  let config = step.screen_config || step.screenConfig || {};
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch (e) {
      console.warn('Failed to parse screen_config:', e);
      config = {};
    }
  }
  return config;
}

/**
 * Normalize any screen_config format to a per-screen map:
 * { A1: { view, params } | null, ... } for all 8 wall screens.
 */
export function resolveScreenAssignments(screenConfig) {
  const config = screenConfig || {};
  const screensConfig = config.screens || {};
  const assignments = {};

  const hasPerScreenConfig = ALL_SCREENS.some(code => config[code] || screensConfig[code]);

  ALL_SCREENS.forEach(code => {
    if (hasPerScreenConfig) {
      const entry = config[code] || screensConfig[code];
      assignments[code] = entry
        ? { view: entry.view || entry.type || null, params: entry.params || {} }
        : null;
      return;
    }

    const group = PRIMARY_SCREENS.includes(code) ? screensConfig.primary : screensConfig.secondary;
    const view = group?.view || group?.type || config.walls?.view || null;
    assignments[code] = view ? { view, params: group?.params || {} } : null;
  });

  return assignments;
}

/**
 * Fan out one targeted screen-command per assigned screen.
 * Returns the { code: { currentView } } updates for local state mirroring.
 */
export function applyStepScreenConfig(socket, instanceId, step) {
  if (!step || !socket) return {};

  const assignments = resolveScreenAssignments(parseScreenConfig(step));
  const updates = {};

  Object.entries(assignments).forEach(([code, assignment]) => {
    if (!assignment?.view) return;
    socket.emit('screen-command', {
      targetScreen: code,
      instanceId,
      command: 'switch-view',
      payload: {
        view: assignment.view,
        viewParams: assignment.params?.mediaContent, // ScreenClient expects viewParams for media
        mediaContent: assignment.params?.mediaContent,
      },
    });
    updates[code] = { currentView: assignment.view };
  });

  return updates;
}

/** Expand an apply-to target into concrete screen codes. */
export function expandTarget(target, code) {
  switch (target) {
    case 'screen': return [code];
    case 'wall': return WALLS.find(w => w.screens.includes(code))?.screens || [code];
    case 'primary': return PRIMARY_SCREENS;
    case 'secondary': return SECONDARY_SCREENS;
    case 'all': return ALL_SCREENS;
    default: return [code];
  }
}
